import { useContext } from 'react';
import { AxiosResponse } from 'axios';

import { AuthState } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';

import { StaleSessionContext } from '../app/StaleSessionContext/StaleSessionContext';
import { config } from '../config/env';
import { securityWorker } from '../models/Security';
import { ApiConfig, HttpClient } from '../services/backend/http-client';

/**
 * Base hook for creating authenticated API clients
 * Centralizes authentication logic and configuration
 */
export const useBaseApiClient = <T extends HttpClient<AuthState>>(
  ClientClass: new (config: ApiConfig<AuthState>) => T
): T => {
  const { oktaAuth } = useOktaAuth();
  const { markSessionStale } = useContext(StaleSessionContext);

  const client = new ClientClass({
    securityWorker,
    baseURL: config.api.baseUrl,
  });

  if (oktaAuth.authStateManager.getAuthState()?.isAuthenticated) {
    client.setSecurityData(oktaAuth.authStateManager.getAuthState());
  }

  oktaAuth.authStateManager.subscribe((authState) => {
    client.setSecurityData(authState);
  });

  client.instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: unknown) => {
      if ((error as { response?: { status?: number } }).response?.status === 401) {
        markSessionStale();
      }
      return Promise.reject(error);
    }
  );

  return client;
};
